import type { PropsWithChildren } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export const sectionEyebrowSizes = ['xs', 'sm'] as const;
export type SectionEyebrowSize = (typeof sectionEyebrowSizes)[number];

const sizeClasses: Record<SectionEyebrowSize, string> = {
  /** 10px — used inside dense surfaces (mode-journey labels, tight cards). */
  xs: 'text-[10px]',
  /** 12px — standard section label inside panels and modals. */
  sm: 'text-xs',
};

export interface SectionEyebrowProps extends PropsWithChildren {
  size?: SectionEyebrowSize;
  /** HTML element to render. Defaults to `<p>` for clean block layout.
   * Use `<h3>` when the label is a true section heading (a11y signal).
   * Use `<span>` when nesting inside another block element. */
  as?: 'p' | 'span' | 'h3';
  className?: ClassName;
}

/**
 * Small uppercase-tracking section label — "TRIPS", "SEARCH RADIUS",
 * "CATEGORY DISPLAY", etc. Consolidates the 19+ inline duplications of
 * `text-xs font-semibold uppercase tracking-wider text-content-subtle`.
 *
 * Tracking is normalized to `tracking-wider` everywhere — the prior
 * codebase had `tracking-wider` and `tracking-widest` mixed at random.
 */
export const SectionEyebrow = ({
  size = 'sm',
  as = 'p',
  className,
  children,
}: SectionEyebrowProps) => {
  // `font-body` is explicit because theme.css applies `font-heading`
  // (Fraunces) globally to h1/h2/h3 — when as="h3" was used we were
  // inheriting that and rendering the eyebrow in a serif, which read
  // as a layout bug. The eyebrow is a uniform sans-serif label
  // regardless of the rendered element.
  const classes = twx(
    'font-body font-semibold uppercase tracking-wider text-content-subtle',
    sizeClasses[size],
    className,
  );
  if (as === 'span') {
    return <span className={classes}>{children}</span>;
  }
  if (as === 'h3') {
    return <h3 className={classes}>{children}</h3>;
  }
  return <p className={classes}>{children}</p>;
};
